import { payeeStrings } from '@data/paymentSlip';
import { paymentSlipBatchStrings } from '@data/paymentSlipBatch';
import { FileTypeHelper, LocalFile, RemoteFile } from '@models/next/FileType';
import { PaginationType } from '@models/next/PaginationType';
import {
  Payee,
  PaymentDetailsHelper,
  PaymentSlip,
  PaymentSlipSearchQuery,
  examplePaymentSlips,
} from '@models/next/PaymentSlip';
import { MessagesEither, firstLeftEither, makeMessagesEitherRightIf } from '@utils/errors';
import { f } from '@utils/nextForms';
import { roundPrice } from '@utils/types';
import * as r from 'remeda';
import * as z from 'zod';

export const PaymentSlipBatchPaymentStatusName = z.enum(['未', '済']);
export type PaymentSlipBatchPaymentStatusName = z.infer<typeof PaymentSlipBatchPaymentStatusName>;

export const PaymentSlipBatch = z.object({
  id: z.number(),
  batch_no: z.string().label(paymentSlipBatchStrings.batch_no),
  payment_slips: PaymentSlip.array(),
  payment_date: f.dateString().label(paymentSlipBatchStrings.payment_date),
  deadline: f.dateString().label(paymentSlipBatchStrings.deadline),
  payee: Payee.label(paymentSlipBatchStrings.payee),
  payment_status: PaymentSlipBatchPaymentStatusName.optional().label(
    paymentSlipBatchStrings.payment_status,
  ),
  invoice_files: RemoteFile.array().label(paymentSlipBatchStrings.invoice_files),
  withdraw_no: z.string().nullable().label(paymentSlipBatchStrings.withdraw_no),
  withdraw_date: f.dateString().nullable().label(paymentSlipBatchStrings.withdraw_date),
  withdraw_certificate_files: RemoteFile.array().label(
    paymentSlipBatchStrings.withdraw_certificate_files,
  ),
});

export type PaymentSlipBatch = z.infer<typeof PaymentSlipBatch>;

export const PaymentSlipBatchForm = z
  .object({
    batch_no: PaymentSlipBatch.shape.batch_no,
    payment_slip_ids: z.array(z.number()),
    payment_date: PaymentSlipBatch.shape.payment_date,
    deadline: PaymentSlipBatch.shape.deadline,
    payee: PaymentSlipBatch.shape.payee,
    invoice_files: LocalFile.array().label(paymentSlipBatchStrings.invoice_files),
    withdraw_no: PaymentSlipBatch.shape.withdraw_no,
    withdraw_date: PaymentSlipBatch.shape.withdraw_date,
    withdraw_certificate_files: LocalFile.array().label(
      paymentSlipBatchStrings.withdraw_certificate_files,
    ),
  })
  .superRefine(({ withdraw_no, withdraw_date }, ctx) => {
    if (withdraw_no && !withdraw_date) {
      ctx.addIssue({
        code: 'custom',
        path: ['withdraw_date'],
        message: paymentSlipBatchStrings.withdrawDateRequired,
      });
    }
  });

export type PaymentSlipBatchForm = z.infer<typeof PaymentSlipBatchForm>;

export const PaymentSlipBatchSearchQuery = z
  .object({
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
    ordering: z.string().array(),
    batch_no: PaymentSlipBatch.shape.batch_no,
    payee: z.string().label(payeeStrings.payee),
    payment_date_after: PaymentSlipBatch.shape.payment_date,
    payment_date_before: PaymentSlipBatch.shape.payment_date,
    deadline_after: PaymentSlipBatch.shape.deadline,
    deadline_before: PaymentSlipBatch.shape.deadline,
    payment_status: PaymentSlipBatchPaymentStatusName.label(paymentSlipBatchStrings.payment_status),
  })
  .partial();

export type PaymentSlipBatchSearchQuery = z.infer<typeof PaymentSlipBatchSearchQuery>;

export const PaymentSlipBatchPages = z.object({
  count: z.number(),
  results: PaymentSlipBatch.array(),
});

export type PaymentSlipBatchPages = z.infer<typeof PaymentSlipBatchPages>;

export const PaymentSlipBatchPost = PaymentSlipBatchForm.innerType()
  .pick({
    payment_slip_ids: true,
    payment_date: true,
    deadline: true,
    payee: true,
    withdraw_no: true,
    withdraw_date: true,
  })
  .extend({
    invoice_files: RemoteFile.array().label(paymentSlipBatchStrings.invoice_files),
    withdraw_certificate_files: RemoteFile.array().label(
      paymentSlipBatchStrings.withdraw_certificate_files,
    ),
  });

export type PaymentSlipBatchPost = z.infer<typeof PaymentSlipBatchPost>;

export const PaymentSlipBatchPatch = PaymentSlipBatchPost.partial();

export type PaymentSlipBatchPatch = z.infer<typeof PaymentSlipBatchPatch>;

export const PaymentSlipBatchSearchType = z.object({
  id: z.number(),
  batchNo: z.string(),
});

export const PaymentSlipBatchResponse = PaymentSlipBatch.pick({
  id: true,
  batch_no: true,
});

export type PaymentSlipBatchResponse = z.infer<typeof PaymentSlipBatchResponse>;

export const initialPaymentSlipSearchQueryForBatch: PaymentSlipSearchQuery = {
  page: 1,
  page_size: 10,
  payment_status: '未',
  has_batch_no: false,
  status: '作成済',
};

export class PaymentSlipBatchHelper {
  constructor(public data: PaymentSlipBatch) {}

  toForm(): PaymentSlipBatchForm {
    return {
      ...this.data,
      payment_slip_ids: this.data.payment_slips.map((ps) => ps.id),
      invoice_files: this.data.invoice_files.map((file) => FileTypeHelper.remoteToLocal(file)),
      withdraw_certificate_files: this.data.withdraw_certificate_files.map((file) =>
        FileTypeHelper.remoteToLocal(file),
      ),
    };
  }

  getTotalAmount() {
    return roundPrice(
      r.sumBy(this.data?.payment_slips ?? [], (ps) =>
        r.sumBy(ps.payment_details, (item) =>
          new PaymentDetailsHelper(item).getTatekaeTotalPrice(),
        ),
      ),
      'JPY',
    );
  }

  canEdit(): MessagesEither {
    return makeMessagesEitherRightIf(this.data.payment_status === '未', {
      messages: [paymentSlipBatchStrings.cannotEditPaidBatch],
    });
  }

  static canCreateBatch(paymentSlips: PaymentSlip[]): MessagesEither {
    return firstLeftEither(
      () =>
        makeMessagesEitherRightIf(paymentSlips.length > 0, {
          messages: [paymentSlipBatchStrings.pleaseSelectPaymentSlip],
        }),
      () =>
        makeMessagesEitherRightIf(
          paymentSlips.every((ps) => r.equals(ps.payee, paymentSlips[0].payee)),
          {
            messages: [paymentSlipBatchStrings.payeeMustBeTheSame],
          },
        ),
      () =>
        makeMessagesEitherRightIf(
          paymentSlips.every((ps) => r.last(ps.payment_status)?.name !== '済'),
          {
            messages: [paymentSlipBatchStrings.paymentSlipMustNotBePaid],
          },
        ),
      () =>
        makeMessagesEitherRightIf(
          paymentSlips.every((ps) => r.last(ps.status)?.name === '作成済'),
          {
            messages: [paymentSlipBatchStrings.paymentSlipMustBeCreated],
          },
        ),
      () =>
        makeMessagesEitherRightIf(
          paymentSlips.every((ps) => !ps.batch?.batch_no),
          {
            messages: [paymentSlipBatchStrings.paymentSlipAlreadyBatched],
          },
        ),
    );
  }

  static resolveBatchFromPaymentSlips(paymentSlips: PaymentSlip[]): PaymentSlipBatch {
    return {
      id: 0,
      batch_no: '',
      payee: r.first(paymentSlips)?.payee ?? Payee.getFormMeta().getInitialOutput(),
      payment_slips: paymentSlips,
      payment_date: r.first(paymentSlips)?.payment_date ?? '',
      deadline: r.first(paymentSlips)?.cutoff_date ?? '',
      invoice_files: [],
      withdraw_no: '',
      withdraw_date: '',
      withdraw_certificate_files: [],
      payment_status: '未',
    };
  }
}

export const examplePaymentSlipBatchDetails: PaymentSlipBatch = {
  id: 1,
  batch_no: '1234567',
  payment_date: '2021-01-01',
  deadline: '2021-01-01',
  payee: {
    id: 1,
    name_ja: '株式会社テスト',
    name_en: 'カブシキガイシャテスト',
    principal_name: 'テスト太郎',
    fc_no: '1234567',
    phone: '09012345678',
    email: '',
    partner_id: 1,
    registration_number: '1234567',
  },
  invoice_files: [],
  withdraw_no: '1234567',
  withdraw_date: '2021-01-01',
  withdraw_certificate_files: [],
  payment_slips: examplePaymentSlips,
  payment_status: '済',
};

export const examplePaymentSlipBatchPages: PaymentSlipBatchPages = {
  count: 1,
  results: [
    {
      id: 1,
      batch_no: '1234567',
      payment_date: '2021-01-01',
      deadline: '2021-01-01',
      payee: {
        id: 1,
        name_ja: '株式会社テスト',
        name_en: 'カブシキガイシャテスト',
        principal_name: 'テスト太郎',
        fc_no: '1234567',
        phone: '09012345678',
        email: '',
        partner_id: 1,
        registration_number: '1234567',
      },
      invoice_files: [],
      withdraw_no: '1234567',
      withdraw_date: '2021-01-01',
      withdraw_certificate_files: [],
      payment_slips: examplePaymentSlips,
      payment_status: '済',
    },
    {
      id: 2,
      batch_no: '1234567',
      payment_date: '2021-01-01',
      deadline: '2021-01-01',
      payee: {
        id: 1,
        name_ja: '株式会社テスト',
        name_en: 'カブシキガイシャテスト',
        principal_name: 'テスト太郎',
        fc_no: '1234567',
        phone: '09012345678',
        email: '',
        partner_id: 1,
        registration_number: '1234567',
      },
      invoice_files: [],
      withdraw_no: '1234567',
      withdraw_date: '2021-01-01',
      withdraw_certificate_files: [],
      payment_slips: examplePaymentSlips,
      payment_status: '済',
    },
  ],
};
